/* eslint-disable max-lines */
'use client';

import {
	type ApiErrorResponse,
	EventIdentifier,
	FeatureTeamEvent,
	noop,
	NotificationService,
	TrackingEventType,
	useAppLoader,
	useCountdown,
	useDeviceId,
	useIntl,
	useServices,
	useSettings
} from '@capsicum/common';
import {
	Alert,
	BaseCodeInput,
	type BaseCodeInputRef,
	CondensedButton,
	Notification,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import {
	type CodeInputError,
	getCodeInputError,
	showEmailInputFormAtom,
	useCodeVerification,
	usePhoneNumberVerificationChecker,
	useRegistrationVerification,
	useSendCodeByEmail,
	useSendCodeByWhatsapp,
	useSendVerificationCode
} from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { type FunctionComponent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useTriggerCaptcha } from '../../hooks/useTriggerCaptcha';
import { type CodeVerificationProps } from '../../models';
import { Shell } from '../../ui/Shell';
import GeeTestContainer, { type GeeTestRef } from '../user-details/GeeTestContainer/GeeTestContainer';

import { CodeInputErrorMessage } from './CodeInputErrorMessage';
import { Link } from './CodeVerification.styled';
import { Header } from './Header';
import { LoginTimer } from './LoginTimer';

export const CodeVerification: FunctionComponent<CodeVerificationProps> = ({
	formattedValues,
	onRegistrationRedirection,
	onUserDetailsRedirection,
	variant
}) => {
	// State
	const captchaRef = useRef<GeeTestRef>(null);
	const [code, setCode] = useState<string>('');
	const sendCodeByWhatsapp = useSendCodeByWhatsapp();
	const [geetestReady, setGeetestReady] = useState(false);
	const [isUserDisabled, setIsUserDisabled] = useState(false);
	const setShowEmailInput = useSetAtom(showEmailInputFormAtom);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const isUsingPhoneNumber = formattedValues.type === 'phone-number';
	const [alreadySentByEmail, setAlreadySentByEmail] = useState(false);
	const [codeInputError, setCodeInputError] = useState<CodeInputError>();
	const [alreadySentByWhatsapp, setAlreadySentByWhatsapp] = useState(false);

	// Hooks
	const { $t } = useIntl();
	const { replace, searchParams } = useRouter();
	const appLoader = useAppLoader();
	const sendCodeByEmail = useSendCodeByEmail();
	const { analytics, setUserId } = useServices();
	const inputCodeRef = useRef<BaseCodeInputRef>(null);
	const { sendEmailCodeByMobileNumberToUser } = useSendVerificationCode();
	const { verifyAndLoginEmail, verifyAndLoginSms } = useCodeVerification();
	const triggerCaptcha = useTriggerCaptcha({ captcha: captchaRef.current });
	const { registrationVerificationEmail, registrationVerificationSMS } = useRegistrationVerification();
	const {
		settings: { IsWhatsAppCodesEnabled }
	} = useSettings();
	const deviceUid = useDeviceId() as string;

	const isPhoneNumberVerified = usePhoneNumberVerificationChecker({
		formattedValues,
		variant
	});

	const reminderCountdown = useCountdown({
		immediate: true,
		minutes: 0,
		seconds: 60
	});

	// useCallbacks
	const resetToWelcomeScreenOnMobileMode = useCallback(() => {
		setShowEmailInput(false);
		onUserDetailsRedirection();
	}, [onUserDetailsRedirection, setShowEmailInput]);

	const sendCodeToEmailBySMS = useCallback(async () => {
		if (formattedValues) {
			const {
				medium: { countryCode, mobileNumber }
			} = formattedValues;

			return countryCode && mobileNumber
				? (async () => {
						try {
							await appLoader(
								sendEmailCodeByMobileNumberToUser({
									countryCode: countryCode,
									mobileNumber: mobileNumber
								})
							);
						} catch (err) {
							return Promise.reject(err);
						}
					})()
				: Promise.reject(new Error('validationParams are undefined'));
		}
	}, [formattedValues, appLoader, sendEmailCodeByMobileNumberToUser]);

	const onSendCodeToEmailByEmail = useCallback(
		async () =>
			!isUsingPhoneNumber &&
			sendCodeByEmail({
				attempt: '0',
				captchaInformation: await triggerCaptcha(),
				email: formattedValues.medium.email,
				variant
			}),
		[isUsingPhoneNumber, sendCodeByEmail, formattedValues.medium.email, variant, triggerCaptcha]
	);

	const onSendByEmailHandler = useCallback(
		() =>
			(isUsingPhoneNumber ? sendCodeToEmailBySMS() : onSendCodeToEmailByEmail())
				.then(() => {
					NotificationService.addNotification({
						component: (
							<Notification
								message={$t('codeVerification.emailSent', {
									email: formattedValues.medium.email
								})}
								title={$t('codeVerification.emailSentTitle')}
								variant="success"
							/>
						)
					});
					reminderCountdown.reset();
					setAlreadySentByEmail(true);
				})
				.catch(noop),
		[
			isUsingPhoneNumber,
			sendCodeToEmailBySMS,
			onSendCodeToEmailByEmail,
			$t,
			formattedValues.medium.email,
			reminderCountdown
		]
	);

	const onSendCodeToWhatsapp = useCallback(async () => {
		setAlreadySentByWhatsapp(true);
		const captchaInformation = await triggerCaptcha();

		try {
			await sendCodeByWhatsapp(
				formattedValues.type === 'phone-number'
					? {
							data: {
								attempt: '0',
								captchaInformation,
								countryCode: formattedValues.medium.countryCode,
								mobileNumber: formattedValues.medium.mobileNumber
							},
							type: formattedValues.type
						}
					: {
							data: {
								attempt: '0',
								captchaInformation,
								email: formattedValues.medium.email
							},
							type: formattedValues.type
						}
			);

			NotificationService.addNotification({
				component: (
					<Notification
						message={$t('codeVerification.whatsAppSent')}
						title={$t('codeVerification.whatsAppSentTitle')}
						variant="success"
					/>
				)
			});
		} catch {
			NotificationService.addNotification({
				component: (
					<Notification
						message={$t('codeVerification.whatsAppSentError')}
						title={$t('codeVerification.whatsAppSentErrorTitle')}
						variant="error"
					/>
				)
			});
		}
	}, [
		$t,
		triggerCaptcha,
		sendCodeByWhatsapp,
		formattedValues.type,
		formattedValues.medium.email,
		formattedValues.medium.countryCode,
		formattedValues.medium.mobileNumber
	]);

	const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
			e.preventDefault();
		}
	}, []);

	const handleVerifyAndLogin = useCallback(async () => {
		const loginResponse = await appLoader(
			isUsingPhoneNumber
				? verifyAndLoginSms({
						deviceName: 'webapp',
						deviceUid,
						generatedCode: code,
						mobileNumber: formattedValues.medium.mobileNumber
					})
				: verifyAndLoginEmail({
						deviceUid,
						email: formattedValues.medium.email,
						generatedCode: code
					})
		);

		setUserId(loginResponse?.userId || '');
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.LoginEnd, {
			action: 'login',
			feature_team: FeatureTeamEvent.OFT,
			status: 'success'
		});
		analytics.sendEvent(TrackingEventType.identify, loginResponse?.userId || '');
		replace('/?' + searchParams.toString());
	}, [
		analytics,
		appLoader,
		code,
		formattedValues.medium.email,
		formattedValues.medium.mobileNumber,
		isUsingPhoneNumber,
		replace,
		verifyAndLoginEmail,
		verifyAndLoginSms,
		deviceUid
	]);

	const handleRegistrationVerification = useCallback(async () => {
		const response = await appLoader(
			isUsingPhoneNumber
				? registrationVerificationSMS({
						generatedCode: code,
						mobileNumber: formattedValues.medium.mobileNumber
					})
				: registrationVerificationEmail({
						email: formattedValues.medium.email, // substitute mobileNumer with email
						generatedCode: code
					})
		);

		if (response.error) {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
				action: 'sign_up',
				feature_team: FeatureTeamEvent.OFT,
				status: 'failure'
			});
			setCodeInputError('generic');
		} else {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
				action: 'sign_up',
				feature_team: FeatureTeamEvent.OFT,
				status: 'success'
			});
			onRegistrationRedirection && onRegistrationRedirection({ verificationCode: code });
		}
	}, [
		analytics,
		appLoader,
		code,
		formattedValues.medium.email,
		formattedValues.medium.mobileNumber,
		isUsingPhoneNumber,
		onRegistrationRedirection,
		registrationVerificationEmail,
		registrationVerificationSMS
	]);

	// useEffects
	useEffect(() => {
		if (code.length !== 6) return;

		(async () => {
			inputCodeRef.current?.blur();
			setIsSubmitLoading(true);
			try {
				// if user is registered, then login, otherwise go to registration
				await (!onRegistrationRedirection ? handleVerifyAndLogin : handleRegistrationVerification)();
			} catch (e) {
				inputCodeRef.current?.focus();
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingSmsCode, {
					action: 'login',
					feature_team: FeatureTeamEvent.OFT,
					status: 'failure'
				});
				const error: ApiErrorResponse = (e as any)?.response?.data;

				setCodeInputError(getCodeInputError(error));
			} finally {
				setIsSubmitLoading(false);
			}
		})();
	}, [analytics, code.length, handleRegistrationVerification, handleVerifyAndLogin, onRegistrationRedirection]);

	useEffect(() => {
		codeInputError === 'disabled-account' && setIsUserDisabled(true);
	}, [codeInputError]);

	// JSX
	const GetRetryComponents = () =>
		!isUserDisabled &&
		(!reminderCountdown.seconds || reminderCountdown.seconds <= 0 ? (
			codeInputError === 'generic' ? (
				<S.RequestNewCodeBottom>
					{$t<JSX.Element>('auth.welcome.codeInput.pleaseTryAgain', {
						link: chunks => (
							<Link data-testid="codeInput-tryAgain-link" onClick={onUserDetailsRedirection}>
								{chunks}
							</Link>
						)
					})}
				</S.RequestNewCodeBottom>
			) : (
				<>
					<S.ResendSMST>{$t('auth.welcome.codeInput.retrySMS')}</S.ResendSMST>
					<S.CenterWrapper>
						<CondensedButton
							color="neutral"
							disabled={!geetestReady || (!isUsingPhoneNumber && alreadySentByEmail)}
							isLoading={!geetestReady}
							key="send-again"
							size="small"
							testID="codeInput-sendAgain-button"
							text={isUsingPhoneNumber ? $t('auth.sendBySms2') : $t('auth.sendByEmail2')}
							variant="outlined"
							onClick={isUsingPhoneNumber ? onUserDetailsRedirection : onSendByEmailHandler}
						/>
					</S.CenterWrapper>
				</>
			)
		) : (
			<S.CenterWrapper>
				<LoginTimer
					label={
						isUsingPhoneNumber
							? 'auth.welcome.codeInput.timerMessageWithSMS'
							: 'auth.welcome.codeInput.timerMessageWithEmail'
					}
					seconds={reminderCountdown.seconds}
				/>
			</S.CenterWrapper>
		));

	const otherMethodsActions = useMemo(() => {
		if (variant !== 'login' || isUserDisabled || (reminderCountdown.seconds && reminderCountdown.seconds > 50))
			return [];

		const actions: Array<JSX.Element> = [];

		if (!isUsingPhoneNumber && isPhoneNumberVerified) {
			actions.push(
				<CondensedButton
					color="neutral"
					key="send-by-sms"
					size="small"
					testID="codeInput-otherMethodsSMS-button"
					text={$t('auth.sendBySms2')}
					variant="outlined"
					onClick={resetToWelcomeScreenOnMobileMode}
				/>
			);
		}

		if (isUsingPhoneNumber && formattedValues.medium.email) {
			actions.push(
				<CondensedButton
					color="neutral"
					disabled={alreadySentByEmail}
					key="send-by-email"
					size="small"
					testID="codeInput-otherMethodsEmail-button"
					text={$t('auth.sendByEmail2')}
					variant="outlined"
					onClick={onSendByEmailHandler}
				/>
			);
		}

		if (IsWhatsAppCodesEnabled && isPhoneNumberVerified && geetestReady) {
			actions.push(
				<CondensedButton
					color="neutral"
					disabled={alreadySentByWhatsapp}
					key="send-by-whatsapp"
					size="small"
					testID="codeInput-otherMethodsWhatsapp-button"
					text={$t('auth.sendByWhatsApp')}
					variant="outlined"
					onClick={onSendCodeToWhatsapp}
				/>
			);
		}

		return actions;
	}, [
		$t,
		variant,
		geetestReady,
		isUserDisabled,
		reminderCountdown,
		alreadySentByEmail,
		isUsingPhoneNumber,
		onSendByEmailHandler,
		onSendCodeToWhatsapp,
		isPhoneNumberVerified,
		alreadySentByWhatsapp,
		IsWhatsAppCodesEnabled,
		formattedValues.medium.email,
		resetToWelcomeScreenOnMobileMode
	]);

	return (
		<Shell>
			<S.Wrapper>
				<Header
					value={isUsingPhoneNumber ? formattedValues.medium.mobileNumber : formattedValues.medium.email}
				/>

				<S.StyledCodeInput
					codeLength={6}
					disabled={isSubmitLoading || isUserDisabled}
					hasError={!!codeInputError}
					name="code"
					pattern="[0-9]*"
					ref={inputCodeRef}
					testID="codeInput-code-input"
					type="tel"
					value={code}
					onChange={setCode}
					onKeyDown={handleKeyDown}
					onOvershoot={c => {
						setCode(c.substring(c.length - 1, c.length));
						setCodeInputError(undefined);
					}}
				/>
				<CodeInputErrorMessage codeInputError={codeInputError} />

				<GetRetryComponents />

				{otherMethodsActions.length > 0 && (
					<S.OtherMethods>
						<S.OtherMethodsLabel>{$t('auth.welcome.codeInput.otherOptions')}</S.OtherMethodsLabel>
						<S.OtherMethodsActions>{otherMethodsActions}</S.OtherMethodsActions>
					</S.OtherMethods>
				)}

				<S.CodeInputAlert
					closable={false}
					icon={InfoIcon}
					subTitle={$t('auth.welcome.codeInput.info')}
					testID="codeInput-info-alert"
					type="message"
				/>
				<GeeTestContainer ref={captchaRef} onReady={() => setGeetestReady(true)} />
			</S.Wrapper>
		</Shell>
	);
};

namespace S {
	export const Wrapper = styled.div`
		width: 380px;
		align-self: center;
		padding: ${({ theme }) => theme.spacings.medium};
	`;

	export const RequestNewCodeBottom = styled.div`
		margin-top: 0;
		margin-bottom: 0;
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		margin-top: ${({ theme }) => theme.spacings.xlarge};
		margin-bottom: ${({ theme }) => theme.spacings.xlarge};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledCodeInput = styled(BaseCodeInput)`
		justify-content: center;
	`;

	export const ResendSMST = styled.p`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		margin-top: ${({ theme }) => theme.spacings.large};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const CodeInputAlert = styled(Alert)`
		margin-top: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const CenterWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;

	export const OtherMethods = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		margin-top: ${({ theme }) => theme.spacings.large};
	`;

	export const OtherMethodsLabel = styled.p`
		margin: 0;
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const OtherMethodsActions = styled.div`
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
